import { ApiClient, ApiClientOptions, type ApiKeyType } from "@quotientjs/core"
import { StorageProvider } from "@quotientjs/browser-storage"
import { DeviceIdStore } from "./stores/device-id"
import { SessionIdStore } from "./stores/session-id"
import { BrowserFingerprintStore } from "./stores/browser-fingerprint"
import { SDK_VERSION } from "./version"

const SESSION_ID_HEADER = "X-Quotient-Session-Id"
const DEVICE_ID_HEADER = "X-Quotient-Device-Id"
const BROWSER_FINGERPRINT_HEADER = "X-Quotient-Browser-Fingerprint"
const REFERRER_URL_HEADER = "X-Quotient-Referrer-Url"

/**
 * Browser-specific API client that extends the core ApiClient
 * with browser storage and device tracking.
 *
 * `personId` deliberately isn't sent as a header here — analytics events
 * carry it in the request body instead (see `Analytics` in
 * `clients/analytics.ts`).
 */
export class BrowserApiClient extends ApiClient {
  private provider?: StorageProvider
  private analyticsSessionId?: string
  // undefined: Analytics hasn't set this yet, so fall back to document.referrer.
  // null: the active session has no entry referrer — send no header at all,
  // regardless of this tab's own document.referrer.
  private referrerUrl?: string | null

  constructor(options: ApiClientOptions) {
    options.supportedKeyTypes = ["public" as ApiKeyType]
    // register this package in the SDK version chain, between any wrappers
    // (e.g. @quotientjs/react) and @quotientjs/core
    options.sdkVersions = [
      ...(options.sdkVersions ?? []),
      { name: "@quotientjs/client", version: SDK_VERSION },
    ]
    super(options)
  }

  setStorageProvider(provider: StorageProvider) {
    this.provider = provider
  }

  /**
   * Uses the active analytics session for request headers even when browser
   * storage is blocked and the session only exists in memory.
   */
  setAnalyticsSessionId(sessionId: string): void {
    this.analyticsSessionId = sessionId
  }

  /**
   * Sets the referrer header for analytics requests. `Analytics` passes the
   * value saved by `AnalyticsSession` so it stays the same during client-side
   * navigation. Pass `null` (not `undefined`) when the session has no
   * referrer, so `getHeaders()` doesn't fall back to this tab's own
   * `document.referrer`.
   */
  setReferrerUrl(url: string | null): void {
    this.referrerUrl = url
  }

  /**
   * Override headers to add browser-specific headers from storage
   */
  protected async getHeaders(): Promise<Record<string, string>> {
    const headers = await super.getHeaders()

    if (!this.provider) {
      return headers
    }

    const fingerprint = BrowserFingerprintStore.get(this.provider)
    if (fingerprint) {
      headers[BROWSER_FINGERPRINT_HEADER] = fingerprint
    }

    const deviceId = DeviceIdStore.get(this.provider)
    if (deviceId) {
      headers[DEVICE_ID_HEADER] = deviceId
    }

    const sessionId =
      this.analyticsSessionId ?? SessionIdStore.get(this.provider)
    if (sessionId) {
      headers[SESSION_ID_HEADER] = sessionId
    }

    const referrerUrl =
      this.referrerUrl === undefined
        ? typeof document !== "undefined"
          ? document.referrer
          : undefined
        : (this.referrerUrl ?? undefined)
    if (referrerUrl) {
      headers[REFERRER_URL_HEADER] = referrerUrl
    }

    return headers
  }
}
